import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import { supabase } from '../../../lib/supabase';

const EMPTY = { brand: 'iPhone', model: '', repair_type: '', price: 0, estimated_time: '1-2小時', in_stock: true };
const BRANDS = ['iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google', '其他'];
const REPAIR_TYPE_SUGGESTIONS = [
  '原廠螢幕', '副廠螢幕', '原廠電池', '副廠電池', '背蓋玻璃更換',
  '原廠 FACE ID', 'FACE ID 維修', 'HOME鍵', '前鏡頭更換', '後鏡頭更換',
  '開機鍵/音量鍵', '相機鍵', '充電孔（響鈴振動）', '聽筒/麥克風',
  '天線/感應模組', '主機板維修', '128G 升級', '256G 升級', '512G 升級',
  '1TB 升級', '2TB 升級', '螢幕更換', '電池更換', '鍵盤更換', '充電口維修', '其他'
];

interface PricingItem {
  id: string;
  brand: string;
  model: string;
  repair_type: string;
  price: number;
  estimated_time: string;
  in_stock: boolean;
}

export default function AdminPricingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PricingItem[]>([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem('admin_auth')) navigate('/admin');
    fetchPricing();
  }, [navigate]);

  const fetchPricing = async () => {
    try {
      const allBrands = ['iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google'];
      const allData: PricingItem[] = [];

      for (const brand of allBrands) {
        const { data: batch, error } = await supabase
          .from('repair_pricing')
          .select('*')
          .eq('brand', brand)
          .order('model', { ascending: true });

        if (error) throw error;
        if (batch) allData.push(...batch);
      }

      allData.sort((a, b) => {
        if (a.brand !== b.brand) return a.brand.localeCompare(b.brand);
        return a.model.localeCompare(b.model);
      });

      setData(allData);
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
      showToast('載入失敗');
    }
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const filtered = data.filter(d =>
    d.model.toLowerCase().includes(search.toLowerCase()) ||
    d.brand.toLowerCase().includes(search.toLowerCase()) ||
    d.repair_type.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    if (!form.model || !form.repair_type || form.price <= 0) { showToast('請填寫完整資料'); return; }
    setLoading(true);
    try {
      if (editId) {
        const { error } = await supabase
          .from('repair_pricing')
          .update(form)
          .eq('id', editId);
        if (error) throw error;
        showToast('已更新');
      } else {
        const { error } = await supabase
          .from('repair_pricing')
          .insert([form]);
        if (error) throw error;
        showToast('已新增');
      }
      await fetchPricing();
      setForm(EMPTY); setEditId(null); setShowForm(false);
    } catch (err) {
      console.error('Failed to save:', err);
      showToast('儲存失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: PricingItem) => {
    const { id, ...rest } = item;
    setEditId(id); setForm(rest); setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此項目？')) return;
    try {
      const { error } = await supabase.from('repair_pricing').delete().eq('id', id);
      if (error) throw error;
      await fetchPricing();
      showToast('已刪除');
    } catch (err) {
      console.error('Failed to delete:', err);
      showToast('刪除失敗');
    }
  };

  return (
    <AdminLayout>
      {toast && <div className="fixed top-6 right-6 z-50 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl">{toast}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">維修報價管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {data.length} 項維修報價</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }} className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">
          + 新增報價
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-orange-100 p-6 mb-6">
          <h2 className="font-bold text-gray-900 mb-5">{editId ? '編輯' : '新增'}維修報價</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">品牌</label>
              <select value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400">
                {BRANDS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">型號 *</label>
              <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="如：iPhone 16 Pro Max" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">維修類型 *</label>
              <input
                list="repair-type-list"
                value={form.repair_type}
                onChange={e => setForm({ ...form, repair_type: e.target.value })}
                placeholder="如：原廠螢幕、1TB 升級..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400"
              />
              <datalist id="repair-type-list">
                {REPAIR_TYPE_SUGGESTIONS.map(r => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">費用 (NT$) *</label>
              <input type="number" value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value) })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">預估時間</label>
              <input value={form.estimated_time} onChange={e => setForm({ ...form, estimated_time: e.target.value })} placeholder="如：1-2小時" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.in_stock} onChange={e => setForm({ ...form, in_stock: e.target.checked })} className="w-4 h-4 accent-orange-500" />
                <span className="text-sm text-gray-700">零件庫存中</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={loading} className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50">{loading ? '儲存中...' : (editId ? '儲存變更' : '新增')}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY); }} className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-xl cursor-pointer whitespace-nowrap">取消</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-50">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜尋品牌、型號或維修類型..." className="w-full max-w-sm border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="text-left px-5 py-3">品牌</th>
                <th className="text-left px-5 py-3">型號</th>
                <th className="text-left px-5 py-3">維修類型</th>
                <th className="text-right px-5 py-3">費用</th>
                <th className="text-left px-5 py-3">時間</th>
                <th className="text-left px-5 py-3">庫存</th>
                <th className="text-right px-5 py-3">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/50">
                  <td className="px-5 py-3.5 text-sm text-gray-600">{item.brand}</td>
                  <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{item.model}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{item.repair_type}</td>
                  <td className="px-5 py-3.5 text-sm font-bold text-orange-500 text-right">NT${item.price.toLocaleString()}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{item.estimated_time}</td>
                  <td className="px-5 py-3.5"><span className={`text-xs px-2 py-0.5 rounded-full ${item.in_stock ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{item.in_stock ? '有貨' : '需訂'}</span></td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(item)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-lg cursor-pointer whitespace-nowrap">編輯</button>
                      <button onClick={() => handleDelete(item.id)} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 text-xs rounded-lg cursor-pointer whitespace-nowrap">刪除</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">找不到相關項目</div>}
        </div>
      </div>
    </AdminLayout>
  );
}